<script setup lang="ts">
import { computed } from 'vue';
import { useDisplay } from 'vuetify';
import { useI18n } from 'vue-i18n';
import { useStation } from '@/store/station';

import StripeCheckout from "@/components/views/payment/partials/StripeCheckout.vue";

const station = useStation();
const { name } = useDisplay();
const { t } = useI18n();
const card_width = computed(() => ["xs", "sm"].includes(name.value) ? "w-100" : "w-50");

const valid_qrcode = computed((): boolean => {
  return (station.qrcode ?? -1) > 0;
});

const base_price = computed((): number | null => {
  const p = station.price;
  if (p === null) {
    return null;
  }
  return p / 100;
});

const imprint = computed((): string => {
  const impr = station.max_wallet_imprint_cents;
  return (impr / 100).toFixed(0);
});

const cp_available = computed((): boolean => ! station.charging);

const fixed_price = 0.25;
const commissions_percentage = 0.015;

const commissions = computed((): number | null => {
  const price = base_price.value;
  if (price === null) {
    return null;
  }
  return price * commissions_percentage;
});

const total_price = computed((): number | null => {
  const price = base_price.value;
  if (price === null) {
    return null;
  }
  const comms = commissions.value;
  return price + comms!;
});

const total_price_str = computed((): string => {
  const total = total_price.value;
  if (total === null) {
    return "???";
  }
  return total.toFixed(3);
});

const price_tooltip = computed((): string[] => {
  const price = base_price.value;
  if (price === null) {
    return [`${t('fixed_costs')}: ???`];
  }
  const fixed = fixed_price;
  const comms = commissions.value;
  const total = total_price.value;
  return [
    `${t('fixed_costs')}: ${fixed.toFixed(2)} €`,
    `${t('cost_per_kWh')}: ${total!.toFixed(3)} €/kWh`,
    `${t('commissions')}: ${comms!.toFixed(3)} €/kWh`
  ];
});
</script>


<template>
  <v-card elevation="10" :class="card_width">
    <v-card-title class="bg-primary text-white font-weight-semibold">
      {{ t("card") }}
    </v-card-title>
    <v-divider />
    <v-card-text :disabled="!cp_available" class="text-info pa-0" ref="card_access">
      <v-slide-x-transition mode="out-in" v-if="cp_available">
        <v-container class="py-2">
          <template v-if="valid_qrcode">
            <template v-if="cp_available">
              <v-row dense>
                <v-col class="ma-0 pa-0" style="text-wrap: nowrap; overflow: hidden">
                  <span  class="font-weight-bold">{{ t('cost_per_kWh') }}: {{ total_price_str }} €/kWh</span>
                </v-col>
                <v-col class="ma-0 pa-0" style="text-align: end; text-wrap: nowrap">
                  {{ t("imprint") }}: {{ imprint }} €
                </v-col>
              </v-row>
              <v-row dense>
                <v-col class="ma-0 pa-0" style="text-wrap: nowrap; overflow: hidden">
                  {{ t('fixed_costs')}}: {{ fixed_price }} €
                  <v-btn icon size="small" density="compact" variant="text">
                    <v-icon icon="mdi-information-outline" />
                      <v-tooltip activator="parent" location="start" open-on-click>
                        {{price_tooltip[0]}}<br />
                        {{price_tooltip[1]}}<br />
                        {{price_tooltip[2]}}
                      </v-tooltip>
                  </v-btn>
                </v-col>
              </v-row>
            </template>
            <v-row dense>
              <v-col>
                <StripeCheckout />
              </v-col>
            </v-row>
          </template>
          <v-row v-else class="pl-5 py-2 font-weight-semibold">
            <v-col  class="pa-2 h-100 w-100">
              {{ t("cp_occupied") }}
            </v-col>
          </v-row>
        </v-container>
      </v-slide-x-transition>
      <v-slide-x-transition mode="out-in" v-else>
        <v-container>
          <v-row>
            <v-col class="d-flex justify-center" style="text-align: center">
              <b>{{ t('cp_occupied') }}</b>
            </v-col>
          </v-row>
        </v-container>
      </v-slide-x-transition>
    </v-card-text>
  </v-card>
</template>

<i18n lang="yaml">
en:
  card: "Charge with credit card"
  price: "Price"
  cp_occupied: "Charge in progress, card payment temporarily disabled."
  imprint: "Pre-authorization"
  fixed_costs:  "Fixed costs"
  cost_per_kWh: "Cost per kWh"
  commissions:  "Of which Stripe fees"
it:
  card: "Pagamento e ricarica"
  price: "Prezzo"
  cp_occupied: "Ricarica in corso, pagamento con carta temporaneamente disabilitato."
  imprint: "Preautorizzazione"
  fixed_costs:  "Costi fissi"
  cost_per_kWh: "Costo per kWh"
  commissions:  "Di cui commissioni Stripe"
fr:
  card: "Recharger par Carte de Credit"
  price: "Prix"
  cp_occupied: "Recharge en cours, paiement par carte temporairement désactivé."
  imprint: "Empreinte"
  fixed_costs:  "Coûts fixes"
  cost_per_kWh: "Coût au kWh"
  commissions:  "Dont frais Stripe"
de:
  card: "Zahlung und aufladung"
  price: "Preis"
  cp_occupied: "Aufladung läuft, Zahlung per Kreditkarte vorübergehend deaktiviert"
  imprint: "Vorautorisierung"
  fixed_costs:  "Fixkosten"
  cost_per_kWh: "Kosten pro kWh"
  commissions:  "Davon Stripe-Gebühren"
nl:
  card: "Betaalkaart gebruiken"
  price: "Prijs"
  cp_occupied: "Opwaardering bezig, kaartbetaling tijdelijk uitgeschakeld."
  imprint: "Pre-autorisatie"
  fixed_costs:  "Vaste kosten"
  cost_per_kWh: "Kosten per kWh"
  commissions:  "Waarvan Stripe-kosten"
</i18n>

<style scoped lang="scss">
</style>

<!-- vim: softtabstop=2:shiftwidth=2
-->
